import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import * as api from '@/api/endpoints'
import { queryKeys } from '@/api/query-keys'
import { useAuthStore } from '@/store/auth'
import { notify } from '@/lib/notify'
import { useMe } from './queries'

/** How often to look for newly won keys while the app is open. */
const POLL_MS = 60_000

const storageKey = (userId: number) => `jlm:seen-prizes:${userId}`

function readSeen(userId: number): Set<number> | null {
  try {
    const raw = localStorage.getItem(storageKey(userId))
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return null
    return new Set(parsed.filter((id): id is number => typeof id === 'number'))
  } catch {
    return null
  }
}

function writeSeen(userId: number, ids: Set<number>) {
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify([...ids]))
  } catch {
    // Storage unavailable: the worst case is announcing a win again.
  }
}

/**
 * Raises a notification when a key the user hasn't seen before turns up in
 * their loot. That's how a giveaway win reaches them while the app is open, or
 * the next time they open it.
 *
 * The first check on a device only records what's already there, so existing
 * keys aren't announced as new.
 */
export function useWinWatcher() {
  const navigate = useNavigate()
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const { data: me } = useMe()
  const userId = me?.id ?? null

  const { data: loot } = useQuery({
    queryKey: queryKeys.myLoot,
    queryFn: api.getMyLoot,
    enabled: isAuthenticated,
    refetchInterval: POLL_MS,
    refetchOnWindowFocus: true,
  })

  useEffect(() => {
    if (!loot || userId === null) return

    const ids = loot.map((prize) => prize.id).filter((id): id is number => id !== null)
    const seen = readSeen(userId)

    if (seen === null) {
      writeSeen(userId, new Set(ids))
      return
    }

    const fresh = loot.filter((prize) => prize.id !== null && !seen.has(prize.id))
    if (fresh.length === 0) return

    for (const prize of fresh) {
      const path = prize.giveaway_path
      notify({
        message: `You won a key for ${prize.title}!`,
        color: 'positive',
        duration: 12_000,
        action: {
          label: path ? 'View' : 'My Loot',
          onClick: () => navigate(path ?? '/my/loot'),
        },
      })
    }
    writeSeen(userId, new Set([...seen, ...ids]))
  }, [loot, userId, navigate])
}
